/*
 * <cyber-countdown> custom element shared by the Cyber countdown sections.
 * Attributes:
 *   data-end        End date, "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM"
 *   data-tz         UTC offset of the end date, e.g. "-03:00"
 *   data-expired    "hide" removes the host section, "hide-self" removes only this element,
 *                   "message" shows [data-expired-message]
 *   data-dismiss-key  sessionStorage key used by a [data-dismiss] button (optional)
 * Children with [data-unit="days|hours|minutes|seconds"] receive the values.
 */
if (!customElements.get('cyber-countdown')) {
  customElements.define(
    'cyber-countdown',
    class CyberCountdown extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section') || this;
        this.end = this.parseEnd();
        this.units = {};
        this.querySelectorAll('[data-unit]').forEach((el) => {
          this.units[el.dataset.unit] = el;
        });

        if (this.isDismissed()) {
          this.section.style.display = 'none';
          return;
        }

        const dismissButton = this.querySelector('[data-dismiss]');
        if (dismissButton) {
          dismissButton.addEventListener('click', () => this.dismiss());
        }

        const buyButton = this.querySelector('[data-scroll-buy]');
        if (buyButton) {
          buyButton.addEventListener('click', (event) => this.scrollToBuy(event));
        }

        if (!this.end) return;
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      parseEnd() {
        let value = (this.dataset.end || '').trim();
        if (!value) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) value += 'T23:59';
        if (/T\d{2}:\d{2}$/.test(value)) value += ':00';
        const tz = (this.dataset.tz || '').trim();
        const date = new Date(value + (/^[+-]\d{2}:\d{2}$/.test(tz) ? tz : ''));
        return isNaN(date.getTime()) ? null : date;
      }

      tick() {
        const diff = this.end.getTime() - Date.now();
        if (diff <= 0) {
          clearInterval(this.timer);
          this.expire();
          return;
        }
        const seconds = Math.floor(diff / 1000);
        this.set('days', Math.floor(seconds / 86400));
        this.set('hours', Math.floor((seconds % 86400) / 3600));
        this.set('minutes', Math.floor((seconds % 3600) / 60));
        this.set('seconds', seconds % 60);
        this.classList.add('is-ready');
      }

      set(unit, value) {
        const el = this.units[unit];
        if (!el) return;
        const text = String(value).padStart(2, '0');
        if (el.textContent !== text) el.textContent = text;
      }

      expire() {
        if (this.dataset.expired === 'message' && this.querySelector('[data-expired-message]')) {
          this.classList.add('is-expired', 'is-ready');
          return;
        }
        // Keep the section visible inside the theme editor so it can still be configured.
        if (window.Shopify && Shopify.designMode) {
          this.classList.add('is-expired', 'is-ready');
          return;
        }
        // "hide-self" is used by blocks inside a bigger section (e.g. product info).
        (this.dataset.expired === 'hide-self' ? this : this.section).style.display = 'none';
      }

      // Scrolls to the product's add-to-cart button; falls back to the link href.
      scrollToBuy(event) {
        const target = Array.from(
          document.querySelectorAll('form[action*="/cart/add"] [type="submit"][name="add"], form[action*="/cart/add"] [type="submit"]')
        ).find((el) => el.offsetParent !== null);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.focus({ preventScroll: true });
      }

      isDismissed() {
        const key = this.dataset.dismissKey;
        if (!key || (window.Shopify && Shopify.designMode)) return false;
        try {
          return sessionStorage.getItem(key) === '1';
        } catch (e) {
          return false;
        }
      }

      dismiss() {
        const key = this.dataset.dismissKey;
        try {
          if (key) sessionStorage.setItem(key, '1');
        } catch (e) {}
        this.section.style.display = 'none';
      }
    }
  );
}
